<template>
    <div>
        {{ callersCount }}
    </div>
</template>

<script setup lang="ts">
import { computed, watch } from 'vue'
import { ALL_QUEUE_ID, QUEUE_RULE_NO_RULE } from '@/enum/redesignQueueDashboardStatistics'
import { limitThresholdValue, warningThresholdValue } from '@/composables/useSoundsThresholdNotification'
import { MaxCallerCount, QueuesFromEntities } from '@/helpers/multiQueueDashboardRedesign'
import { activeCallsQueueMap, sharedAllQueueCalls } from '@/composables/useSharedQueueCallsData'

const emit = defineEmits<{
    (event: 'update-callers-value', payload: MaxCallerCount): void;
    (event: 'clear-queue-max-callers', queueId: number | string): void;
}>()

const props = defineProps<{
    queueId: number | string
    queueName: string
    warningValue: number
    limitValue: number
    filteredQueues: QueuesFromEntities
    queueRuleId?: number | string
}>()

const filteredQueueIds = computed(() => {
    const ids = new Set<number | string>()
    for (const q of props.filteredQueues) ids.add(q.queue_id)
    return ids
})

const callersCount = computed(() => {
    if (props.queueId === ALL_QUEUE_ID) {
        const calls = sharedAllQueueCalls.value
        if (!calls.length) return 0

        const ids = filteredQueueIds.value
        let count = 0
        for (const call of calls) {
            if (ids.has(call.QueueID)) count++
        }
        return count
    }

    const queue = activeCallsQueueMap.value.get(props.queueId as number)
    const calls = queue?.Calls ?? []

    if (!props.queueRuleId) {
        return calls.length
    }

    if (props.queueRuleId === QUEUE_RULE_NO_RULE) {
        return calls.filter(call => !call.queueRule).length
    }
    return calls.filter(call => Number(call.queueRule) === Number(props.queueRuleId)).length
})

function syncCallersThreshold () {
    if (props.queueId === ALL_QUEUE_ID || props.queueRuleId) return

    const value = callersCount.value

    if (value && props.limitValue && value >= props.limitValue) {
        emit('update-callers-value', {
            queueId: props.queueId,
            queueName: props.queueName,
            thresholdStatus: limitThresholdValue,
            value,
        })
        return
    }

    if (value && props.warningValue && value >= props.warningValue) {
        emit('update-callers-value', {
            queueId: props.queueId,
            queueName: props.queueName,
            thresholdStatus: warningThresholdValue,
            value,
        })
        return
    }

    emit('clear-queue-max-callers', props.queueId)
}

watch(() => callersCount.value, () => {
    syncCallersThreshold()
}, { immediate: true })

watch(
    () => [props.warningValue, props.limitValue],
    () => {
        syncCallersThreshold()
    }
)
</script>

<style scoped lang="scss">
</style>
